import api from './api';

export interface Designer {
  id: string;
  name: string;
  description?: string;
  logoPath?: string;
  bannerPath?: string;
  logoUrl?: string;
  bannerUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  dressCount?: number;
}

export interface DesignerListResponse {
  data: Designer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateDesignerRequest {
  name: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateDesignerRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export const designerService = {
  // Get all designers without pagination
  async getDesigners(params?: {
    search?: string;
    showDisabled?: boolean;
  }): Promise<DesignerListResponse> {
    const response = await api.get('/designers', { 
      params: {
        ...params,
        limit: 1000 // Fetch all designers
      }
    });
    return response.data;
  },

  // Get single designer
  async getDesigner(id: string): Promise<Designer> {
    const response = await api.get(`/designers/${id}`);
    return response.data.data;
  },

  // Create designer
  async createDesigner(designer: CreateDesignerRequest): Promise<Designer> {
    const response = await api.post('/designers', designer);
    return response.data.data;
  },

  // Update designer
  async updateDesigner(id: string, updates: UpdateDesignerRequest): Promise<Designer> {
    const response = await api.put(`/designers/${id}`, updates);
    return response.data.data;
  },

  // Delete designer (soft delete)
  async deleteDesigner(id: string): Promise<void> {
    await api.delete(`/designers/${id}`);
  },

  // Upload designer logo
  async uploadLogo(id: string, file: File): Promise<{ message: string; logoPath: string }> {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await api.post(`/designers/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // Upload designer banner
  async uploadBanner(id: string, file: File): Promise<{ message: string; bannerPath: string }> {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await api.post(`/designers/${id}/banner`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // Delete designer logo
  async deleteLogo(id: string): Promise<void> {
    await api.delete(`/designers/${id}/logo`, { data: { confirm: true } });
  },

  // Delete designer banner
  async deleteBanner(id: string): Promise<void> {
    await api.delete(`/designers/${id}/banner`, { data: { confirm: true } });
  },

  // Get image URL
  getImageUrl(path: string | undefined): string | undefined {
    if (!path) return undefined;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    return `${baseUrl}/uploads/${path}`;
  },
};